const HEIF_BRANDS = new Set([
  "heic", "heix", "heim", "heis",
  "hevc", "hevx",
  "mif1", "msf1",
  "heif",
]);

// AVIF is also an ISO-BMFF / MIAF image and commonly declares the generic
// `mif1` brand that HEIC uses, so it would false-positive as HEIC below. It is
// browser-native (decodes in an <img>) and must NOT be routed through the HEIC
// decoder — detect it separately so callers can exclude or reject it.
const AVIF_BRANDS = new Set(["avif", "avis"]);

function fourCC(bytes: Uint8Array, offset: number): string {
  if (offset + 4 > bytes.length) return "";
  return String.fromCharCode(bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3]);
}

/**
 * Parse an ISO-BMFF `ftyp` box and return its brands (major brand first,
 * followed by the compatible brands), or ``null`` if the header is not an
 * `ftyp` box. Pass at least the first ~32 bytes.
 */
function readFtypBrands(header: ArrayBuffer): string[] | null {
  const bytes = new Uint8Array(header);
  if (bytes.length < 12) return null;
  if (fourCC(bytes, 4) !== "ftyp") return null;
  const boxSize = Math.min(
    bytes.length,
    (bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3] || bytes.length,
  );
  const brands = [fourCC(bytes, 8)];
  for (let off = 16; off + 4 <= boxSize && off + 4 <= bytes.length; off += 4) {
    brands.push(fourCC(bytes, off));
  }
  return brands;
}

/**
 * True if the header bytes are an AVIF `ftyp` box (major OR compatible brand is
 * an AVIF brand). Content sniffing — never trust the filename or MIME type.
 */
export function sniffAvif(header: ArrayBuffer): boolean {
  const brands = readFtypBrands(header);
  return brands !== null && brands.some((b) => AVIF_BRANDS.has(b));
}

/**
 * True if the header bytes are an ISO-BMFF `ftyp` box whose major brand OR any
 * compatible brand is a HEIF/HEVC brand. AVIF is explicitly excluded — it shares
 * the generic `mif1` brand but is browser-native, not HEIC. Content sniffing —
 * never trust the filename or MIME type (Android hands HEIC bytes named `.jpg`).
 * Pass at least the first ~32 bytes.
 */
export function sniffHeic(header: ArrayBuffer): boolean {
  const brands = readFtypBrands(header);
  if (brands === null) return false;
  if (brands.some((b) => AVIF_BRANDS.has(b))) return false;
  return brands.some((b) => HEIF_BRANDS.has(b));
}
